import { Injectable, signal, computed, effect } from '@angular/core';

export type ThemeType = 'default' | 'veefin' | 'pbcom';

export interface ThemeConfig {
  name: string;
  displayName: string;
  sidebarTitle: string;
  icon: string;
  color: string;
  loginRoute: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly STORAGE_KEY = 'cib-admin-theme';

  readonly themes: ThemeConfig[] = [
    { name: 'default', displayName: 'DBP', sidebarTitle: 'DBP Admin', icon: 'bank', color: '#1E458E', loginRoute: '/login/dbp' },
    { name: 'veefin', displayName: 'Veefin', sidebarTitle: 'Veefin Admin', icon: 'hexagon', color: '#1AA189', loginRoute: '/login/veefin' },
    { name: 'pbcom', displayName: 'PBCOM', sidebarTitle: 'PBCOM Admin', icon: 'building', color: '#f40202', loginRoute: '/login/pbcom' }
  ];

  private currentTheme = signal<ThemeType>(this.loadTheme());

  readonly theme = computed(() => this.currentTheme());
  readonly isVeefinTheme = computed(() => this.currentTheme() === 'veefin');
  readonly isPbcomTheme = computed(() => this.currentTheme() === 'pbcom');

  constructor() {
    // Apply theme on initialization and whenever it changes
    effect(() => {
      this.applyTheme(this.currentTheme());
    });
  }

  private loadTheme(): ThemeType {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved === 'default' || saved === 'veefin' || saved === 'pbcom') {
        return saved;
      }
    }
    return 'veefin';
  }

  private saveTheme(theme: ThemeType): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(this.STORAGE_KEY, theme);
    }
  }

  private applyTheme(theme: ThemeType): void {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.classList.remove('theme-default', 'theme-veefin', 'theme-pbcom');
      root.classList.add(`theme-${theme}`);
      this.updateFavicon(theme);
    }
  }

  private updateFavicon(theme: ThemeType): void {
    if (typeof document !== 'undefined') {
      const faviconMap: Record<ThemeType, string> = {
        'default': 'assets/images/dbp-logo-v2.svg',
        'veefin': 'assets/images/veefin.png',
        'pbcom': 'assets/images/pb-com-logo.jpg'
      };

      let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.type = theme === 'default' ? 'image/svg+xml' : 'image/png';
      link.href = faviconMap[theme];
    }
  }

  setTheme(theme: ThemeType): void {
    this.currentTheme.set(theme);
    this.saveTheme(theme);
  }

  toggleTheme(): void {
    const themes: ThemeType[] = ['default', 'veefin', 'pbcom'];
    const currentIndex = themes.indexOf(this.currentTheme());
    const nextIndex = (currentIndex + 1) % themes.length;
    this.setTheme(themes[nextIndex]);
  }

  getCurrentTheme(): ThemeType {
    return this.currentTheme();
  }

  getThemeConfig(theme: ThemeType): ThemeConfig | undefined {
    return this.themes.find(t => t.name === theme);
  }

  getCurrentThemeConfig(): ThemeConfig | undefined {
    return this.getThemeConfig(this.currentTheme());
  }

  getLoginRouteForCurrentTheme(): string {
    return this.getCurrentThemeConfig()?.loginRoute || '/login/veefin';
  }
}

